import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Youtube, Facebook, Instagram } from "lucide-react";
import { PARISH, ASSETS, NAV_LINKS } from "@/lib/constants";
import { useContent } from "@/lib/ContentContext";
import { TIDS } from "@/constants/testIds";
import SEO from "@/components/SEO";

/*
 * Route → SEO meta map. Every public route gets a unique title + ~150 char
 * description mentioning Kileleshwa, Nairobi, and the Catholic parish angle.
 */
const ROUTE_META = {
    "/":                 { title: null,
                           description: "Holy Trinity Catholic Church Kileleshwa, a Catholic parish community on Othaya Road, Kileleshwa, Nairobi. Mass times, live stream, prayer wall, daily readings and parish life." },
    "/about":            { title: "About the Parish",
                           description: "About Holy Trinity Catholic Church, Kileleshwa, our story, patronage of the Holy Trinity, clergy, vision, mission and values within the Archdiocese of Nairobi." },
    "/mass":             { title: "Mass Times & Sacraments",
                           description: "Sunday and daily Mass times at Holy Trinity Catholic Church, Kileleshwa, Nairobi. Confessions, sacraments and how to request a Mass intention." },
    "/groups":           { title: "Church Groups & Missions",
                           description: "Explore ministries, Small Christian Communities and youth groups at Holy Trinity Catholic Church Kileleshwa. CWA, CMA, YCA, YSC and 20 SCCs." },
    "/events":           { title: "Events & Calendar",
                           description: "Upcoming events, feast days, celebrations and the parish calendar at Holy Trinity Catholic Church Kileleshwa, a Catholic parish in Nairobi." },
    "/community":        { title: "Community & Daily Prayer",
                           description: "Daily Mass readings, common Catholic prayers, prayer intentions and parishioner reflections from Holy Trinity Catholic Church Kileleshwa." },
    "/prayer-wall":      { title: "Prayer Wall",
                           description: "Submit a prayer intention and pray with the community of Holy Trinity Catholic Church, Kileleshwa, Nairobi." },
    "/live-stream":      { title: "Live Mass Stream",
                           description: "Watch Mass live from Holy Trinity Catholic Church, Kileleshwa, Nairobi, including Sunday liturgies and special celebrations." },
    "/photos":           { title: "Parish Photos",
                           description: "Photos of parish life at Holy Trinity Catholic Church, Kileleshwa, Masses, ministries, celebrations and Small Christian Communities." },
    "/gallery":          { title: "Photo Gallery",
                           description: "Photo gallery of Holy Trinity Catholic Church, Kileleshwa, sanctuary, parish life and ministries in Nairobi." },
    "/blogs":            { title: "Parishioner Blogs",
                           description: "Reflections, testimonies and articles by parishioners of Holy Trinity Catholic Church, Kileleshwa, Archdiocese of Nairobi." },
    "/announcements":    { title: "Announcements",
                           description: "Latest news and parish announcements from Holy Trinity Catholic Church, Kileleshwa, Nairobi, Archdiocese of Nairobi." },
    "/contact":          { title: "Contact the Parish Office",
                           description: "Contact Holy Trinity Catholic Church, Kileleshwa, Othaya Road, Nairobi. Phone, email, map and Mass times for the Parish Office." },
    "/privacy-policy":   { title: "Media & Photography Privacy Policy",
                           description: "Media and photography privacy notice for Holy Trinity Catholic Church, Kileleshwa, how images and video are captured and used at parish events." },
};

const RouteSEO = () => {
    const { pathname } = useLocation();
    // Direct match, then fall back to closest prefix match, then home defaults.
    const meta = ROUTE_META[pathname]
        || Object.entries(ROUTE_META).find(([k]) => k !== "/" && pathname.startsWith(k))?.[1]
        || ROUTE_META["/"];
    return <SEO title={meta.title} description={meta.description} />;
};

const Layout = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const { content } = useContent();
    const H = content.header;
    const F = content.footer;
    const C = content.contact;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    return (
        <div className="min-h-screen flex flex-col paper-bg">
            <RouteSEO />
            {/* Top stained-glass bar */}
            <div className="stained-bar h-1.5 w-full" />

            <header
                className={`sticky top-0 z-40 transition-colors ${
                    scrolled ? "bg-cream-50/95 backdrop-blur border-b border-maroon-100" : "bg-cream-50/80 backdrop-blur"
                }`}
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between gap-4 py-2 sm:py-3">
                        <Link to="/" data-testid={TIDS.nav.logo} className="flex items-center group" aria-label={`${H.siteName}, ${H.subtitle}, ${H.archdiocese}`}>
                            <img
                                src={ASSETS.logo}
                                alt="Holy Trinity Kileleshwa logo"
                                className="h-16 sm:h-20 lg:h-24 w-auto object-contain drop-shadow-[0_3px_10px_rgba(189,110,114,0.55)] transition-transform duration-300 group-hover:scale-[1.04]"
                                height={96}
                                loading="eager"
                                decoding="async"
                            />
                        </Link>

                        <nav className="hidden lg:flex items-center gap-1">
                            {NAV_LINKS.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                                    className={({ isActive }) =>
                                        `relative px-3 py-2 text-sm font-medium transition-colors ${
                                            isActive
                                                ? "text-maroon-800"
                                                : "text-maroon-700/80 hover:text-maroon-800"
                                        }`
                                    }
                                >
                                    {({ isActive }) => (
                                        <>
                                            <span>{l.label}</span>
                                            {isActive && (
                                                <span className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-gold-400 rounded-full" />
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="flex items-center gap-2">
                            <Link
                                to="/live"
                                data-testid="nav-live-cta"
                                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-maroon-600/30 bg-maroon-600 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-cream-50 hover:bg-maroon-700 transition-colors whitespace-nowrap"
                            >
                                <span className="relative flex h-2 w-2">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold-300 opacity-75" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-300" />
                                </span>
                                Watch Live
                            </Link>
                            <button
                                data-testid={TIDS.nav.menuToggle}
                                className="lg:hidden inline-flex items-center justify-center rounded-full border border-maroon-200 p-2 text-maroon-800"
                                onClick={() => setOpen((o) => !o)}
                                aria-label="Toggle menu"
                            >
                                {open ? <X size={20} /> : <Menu size={20} />}
                            </button>
                        </div>
                    </div>

                    {/* Mobile nav */}
                    {open && (
                        <div className="lg:hidden border-t border-maroon-100 py-3 grid gap-1">
                            {NAV_LINKS.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                                    className={({ isActive }) =>
                                        `rounded-lg px-3 py-2 text-sm font-medium ${
                                            isActive
                                                ? "bg-maroon-600 text-cream-50"
                                                : "text-maroon-800 hover:bg-cream-200"
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </div>
                    )}
                </div>
            </header>

            {/* Site-wide tagline bar */}
            <div
                data-testid="colus-tagline-bar"
                className="w-full text-center py-1 border-b border-gold-300/30"
                style={{ background: "linear-gradient(90deg, #7A1F2B, #9A2026, #7A1F2B)" }}
            >
                <span
                    className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.16em] sm:tracking-[0.22em]"
                    style={{ color: "#F3C544" }}
                >
                    A Community Of Love, Unity and Service (COLUS)
                </span>
            </div>

            <main className="flex-1">{children}</main>

            <footer className="mt-16 border-t border-maroon-100 bg-maroon-800 text-cream-50">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid gap-10 md:grid-cols-4">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <img
                                src={ASSETS.logo}
                                alt="logo"
                                className="h-14 w-14 object-contain"
                                width={56}
                                height={56}
                                loading="lazy"
                                decoding="async"
                            />
                            <div>
                                <div className="font-brand text-base sm:text-lg uppercase tracking-wide" style={{ color: "#FFFFFF" }}>{F.brand}</div>
                                <div className="text-[11px]" style={{ color: "#F3EDE4", letterSpacing: "0.14em" }}>{F.brandLine2}</div>
                                <div className="text-[10px] uppercase font-semibold" style={{ color: "#C9A227", letterSpacing: "0.24em" }}>{F.brandLine3}</div>
                            </div>
                        </div>
                        <p className="text-xs max-w-xs italic" style={{ color: "#C9A227", opacity: 0.85 }}>
                            {F.taglineLine}
                        </p>
                    </div>

                    <div className="min-w-0">
                        <h4 className="font-serif text-lg text-gold-300">Visit</h4>
                        <div className="gold-divider my-2 opacity-40" />
                        <p className="text-sm text-cream-50/85 break-words">
                            {C.addressLine1}<br />
                            {C.addressLine2}
                        </p>
                        <a href={PARISH.mapUrl} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm underline decoration-gold-300 underline-offset-4 hover:text-gold-200">
                            Get directions →
                        </a>
                    </div>

                    <div className="min-w-0">
                        <h4 className="font-serif text-lg text-gold-300">Contact</h4>
                        <div className="gold-divider my-2 opacity-40" />
                        <ul className="text-sm space-y-1 text-cream-50/85 break-words">
                            <li>Tel: <a href={`tel:${(C.phone||"").replace(/\s/g, "")}`} className="hover:text-gold-200">{C.phone}</a></li>
                            {C.phone2 && (
                                <li>Tel: <a href={`tel:${(C.phone2||"").replace(/\s/g, "")}`} className="hover:text-gold-200">{C.phone2}</a></li>
                            )}
                            <li className="break-all"><a href={`mailto:${C.emailInfo}`} className="hover:text-gold-200">{C.emailInfo}</a></li>
                            <li className="break-all"><a href={`mailto:${C.emailPriest}`} className="hover:text-gold-200">{C.emailPriest}</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-serif text-lg text-gold-300">Connect</h4>
                        <div className="gold-divider my-2 opacity-40" />
                        <div className="flex items-center gap-3 mt-1">
                            <a href={PARISH.socials.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="footer-youtube" className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-gold-300/40 hover:bg-gold-300/10">
                                <Youtube size={18} />
                            </a>
                            <a href={PARISH.socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-gold-300/40 hover:bg-gold-300/10">
                                <Facebook size={18} />
                            </a>
                            <a href={PARISH.socials.instagramParish} target="_blank" rel="noreferrer" aria-label="Parish Instagram" data-testid="footer-instagram-parish" className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-gold-300/40 hover:bg-gold-300/10" title="@holytrinitykile">
                                <Instagram size={18} />
                            </a>
                            <a href={PARISH.socials.instagramYouth} target="_blank" rel="noreferrer" aria-label="Youth Instagram" data-testid="footer-instagram-youth" className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-gold-300/40 hover:bg-gold-300/10" title="@holytrinitykileleshwa_youth (YCA · YSC)">
                                <Instagram size={18} />
                                <span className="sr-only">Youth Instagram</span>
                            </a>
                        </div>
                        <p className="text-xs text-cream-50/60 mt-4">
                            Follow <span className="text-gold-300">@holytrinitykile</span> for parish updates and <span className="text-gold-300">@holytrinitykileleshwa_youth</span> for YCA · YSC.
                        </p>

                        <p className="text-xs mt-6" style={{ color: "#F3EDE4" }}>
                            <Link
                                to="/privacy-policy"
                                data-testid="footer-privacy-link"
                                className="underline decoration-gold-300 underline-offset-4 hover:text-gold-200"
                            >
                                Media &amp; Photography Privacy Policy
                            </Link>
                        </p>
                    </div>
                </div>
                <div className="border-t border-maroon-700/60">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 text-xs text-cream-50/60 flex flex-col sm:flex-row items-center justify-between gap-2">
                        <span>© {new Date().getFullYear()} Holy Trinity Catholic Church, Kileleshwa. All rights reserved.</span>
                        <span className="italic font-serif text-cream-50/70">Sancta Trinitas, unus Deus, miserere nobis.</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Layout;
